import { Injectable } from '@angular/core';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { RestService } from '../services/common/rest.service';
import { CommonService } from '../services/common/common.service';
import { JwtService } from './jwtservice';
import { ThemeService } from '../services/common/theme.service';
import { ThemeConfig } from '../../appconfig';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class AuthSessionService {

  readonly defaultTheme: ThemeConfig = {
    primaryBackgroundColor: '#BA141A',
    secondaryBackgroundColor: '#ffffff',
    primaryFontColor: '#000000',
    secondaryFontColor: '#ffffff',
    logoUrl: '',
    organizationName: window.location.origin.toLowerCase().indexOf('a2g') > -1 ? 'DocuMap' : 'ERP'
  };

  private apiUrl = environment.apiEndpoint + '/api/';

  constructor(
    private restService: RestService,
    private commonService: CommonService,
    private jwtService: JwtService,
    private themeService: ThemeService,
    private http: HttpClient
  ) {}

  /**
   * Builds auth headers directly — bypasses RestService so we are 100% sure
   * the token is read at call-time and injected correctly.
   */
  private buildHeaders(): HttpHeaders {
    const token = localStorage.getItem('mbcrm/token') || '';
    return new HttpHeaders({
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    });
  }

  /**
   * Called once immediately after a successful login and token storage.
   * Applies org theme immediately (org details are in the login response).
   * Then fetches user context (sites, projects, explorations) in parallel using forkJoin.
   * Uses HttpClient directly to avoid any RestService/interceptor interference.
   *
   * @param userId         The logged-in user's numeric ID
   * @param orgDetails     Organization details from the login response (colors, name, id)
   * @param userGroup      UserGroup details from the login response
   */
  initializeUserSession(
    userId: string | number,
    orgDetails?: any,
    userGroup?: any
  ): void {

    if (!userId) {
      return;
    }

    // --- Apply theme immediately (org details come from login response — no extra API call) ---
    const orgId = orgDetails?.id;
    if (userGroup) {
      this.commonService.setLocalStorageValue('group', JSON.stringify(userGroup));
    }

    if (orgId) {
      this.commonService.getOrganisationLogo(orgId)
        .pipe(catchError(() => of(null)))
        .subscribe(logo => {
          const base64Logo = this.commonService.BaseStringToNormalString(
            logo?.result?.[0]?.base64Content as string
          );
          const theme: ThemeConfig = {
            primaryBackgroundColor: orgDetails?.primaryBackgroundColor || this.defaultTheme.primaryBackgroundColor,
            secondaryBackgroundColor: orgDetails?.secondaryBackgroundColor || this.defaultTheme.secondaryBackgroundColor,
            secondaryFontColor: orgDetails?.secondaryFontColor || this.defaultTheme.secondaryFontColor,
            primaryFontColor: orgDetails?.primaryFontColor || this.defaultTheme.primaryFontColor,
            logoUrl: base64Logo || this.defaultTheme.logoUrl,
            organizationName: orgDetails?.name || this.defaultTheme.organizationName
          };
          this.themeService.applyTheme(theme);
          localStorage.setItem('theme', JSON.stringify(theme));
        });
    } else {
      this.themeService.applyTheme(this.defaultTheme);
      localStorage.setItem('theme', JSON.stringify(this.defaultTheme));
    }

    // --- forkJoin via direct HttpClient calls (bypasses RestService interceptor chain) ---
    // This ensures the calls always go through regardless of token-validation guard in RestService.
    const headers = this.buildHeaders();

    forkJoin({
      sites: this.http.get<any>(`${this.apiUrl}site?userId=${userId}&isActive=true`, { headers })
        .pipe(catchError(() => of(null))),
      projects: this.http.get<any>(`${this.apiUrl}project?userId=${userId}&isActive=true`, { headers })
        .pipe(catchError(() => of(null))),
      explorations: this.http.get<any>(`${this.apiUrl}user-explorations?userId=${userId}`, { headers })
        .pipe(catchError(() => of(null)))
    }).subscribe({
      next: ({ sites, projects, explorations }) => {

        // --- Sites ---
        const rawSites: any[] = sites?.result ?? [];
        const uniqueSites = this.jwtService.getUniqueSitesFromToken(rawSites, 'id');
        this.commonService.setLocalStorageValue('userSites', JSON.stringify(uniqueSites));
        
        // Ensure siteId is set as the first record from userSites post-login
        if (uniqueSites && uniqueSites.length > 0) {
          const firstSiteId = uniqueSites[0].id || uniqueSites[0].siteId;
          this.commonService.setLocalStorageValue('siteId', firstSiteId);
        }

        // --- Projects: map ProjectDto → ProjectSiteDTO-compatible shape ---
        const rawProjects: any[] = projects?.result ?? [];
        const mappedProjects = rawProjects.map((p: any) => {
          // ProjectDto doesn't carry siteName — look it up from rawSites by siteId
          const matchedSite = rawSites.find((s: any) => s.id === p.siteId);
          return {
            projectId: p.id ?? p.projectId,
            id: p.id ?? p.projectId,
            projectName: p.projectName,
            projectDescription: p.projectDescription,
            status: p.status,
            accountId: p.accountId,
            siteId: p.siteId,
            siteName: matchedSite?.siteName ?? p.siteName ?? '',
            explorationId: p.explorationId ?? null,
            landArea: p.landArea,
            landAreaUnit: p.landAreaUnit
          };
        });
        this.commonService.setLocalStorageValue('userProjects', JSON.stringify(mappedProjects));

        // --- Explorations: map UserExplorationGetDto → ExplorationSiteDTO-compatible shape ---
        const rawExplorations: any[] = explorations?.result ?? [];
        const mappedExplorations = rawExplorations.map((e: any) => ({
          explorationId: e.explorationId,
          ExplorationId: e.explorationId,
          name: e.name,
          Name: e.name,
          siteId: e.siteId,
          SiteId: e.siteId,
          isActive: e.isActive,
          status: e.status,
          phase: e.phase,
          description: e.description,
          id: e.id,
          landArea: e.landArea,
          landAreaUnit: e.landAreaUnit
        }));
        this.commonService.setLocalStorageValue('userExplorations', JSON.stringify(mappedExplorations));

        // --- Trigger initial site selection ---
        if (uniqueSites?.length > 0) {
          this.commonService.syncSelectionsBySite(uniqueSites[0]?.id, true);
        }
      },
      error: () => {},
      complete: () => {}
    });
  }

  /**
   * Cache lightweight user details upon login to prevent redundant API calls
   */
  cacheUserDetails(data: any): void {
    if (data?.result?.userDetail) {
      const user = data.result.userDetail;
      const userDetailsCache = {
        id: user.id,
        organizationDetails: user.organizationDetails,
        userGroupDetails: user.userGroupDetails,
        firstName: user.firstName,
        lastName: user.lastName,
        accountId: user.accountId,
        email: user.email,
        primaryPhoneNo: user.primaryPhoneNo,
        userSubTypeId: user.userSubTypeId || user.userSubType?.id,
        isAgent: user.isAgent
      };
      this.commonService.setLocalStorageValue('userDetails', JSON.stringify(userDetailsCache));
    }
  }
}
